import { X, Users } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useApplyTeamDiscount } from '@/hooks/useTeams';

interface TeamDiscountModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  teamName: string;
  userId?: number;
}

type Mode = 'create' | 'existing';

export default function TeamDiscountModal({
  isOpen,
  onClose,
  teamId,
  teamName,
  userId,
}: TeamDiscountModalProps) {
  const [mode, setMode] = useState<Mode>('create');
  const [discount, setDiscount] = useState('');
  const [duration, setDuration] = useState('12');
  const [couponId, setCouponId] = useState('');
  const applyDiscount = useApplyTeamDiscount();
  const toast = useToast();

  if (!isOpen) return null;

  const handleClose = () => {
    setDiscount('');
    setDuration('12');
    setCouponId('');
    setMode('create');
    onClose();
  };

  const handleSubmit = async () => {
    if (mode === 'create') {
      const pct = parseInt(discount);
      const months = parseInt(duration);
      if (!Number.isFinite(pct) || pct < 1 || pct > 100) {
        toast.error('Invalid discount', 'Discount must be 1–100');
        return;
      }
      if (!Number.isFinite(months) || months < 1 || months > 12) {
        toast.error('Invalid duration', 'Duration must be 1–12 months');
        return;
      }
      try {
        const res = await applyDiscount.mutateAsync({
          teamId,
          discount_percentage: pct,
          duration_months: months,
          userId,
        });
        toast.success('Discount applied to team', res.message);
        handleClose();
      } catch (e) {
        toast.error(
          'Failed to apply discount',
          e instanceof Error ? e.message : 'Unknown error'
        );
      }
    } else {
      const id = couponId.trim();
      if (!id) {
        toast.error('Coupon ID required', 'Enter the Stripe coupon ID');
        return;
      }
      try {
        const res = await applyDiscount.mutateAsync({
          teamId,
          coupon_id: id,
          userId,
        });
        toast.success('Coupon attached', res.message);
        handleClose();
      } catch (e) {
        toast.error(
          'Failed to apply discount',
          e instanceof Error ? e.message : 'Unknown error'
        );
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center"
      onClick={handleClose}
    >
      <Card
        className="my-auto w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Apply discount to team {teamName}
              </CardTitle>
              <CardDescription>
                Affects the team's current subscription. All members benefit;
                the discount is not specific to this user.
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <button
              onClick={() => setMode('create')}
              className={`flex-1 px-4 py-2 rounded-md border text-sm transition-colors ${
                mode === 'create'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background hover:bg-muted'
              }`}
            >
              Create new coupon
            </button>
            <button
              onClick={() => setMode('existing')}
              className={`flex-1 px-4 py-2 rounded-md border text-sm transition-colors ${
                mode === 'existing'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background hover:bg-muted'
              }`}
            >
              Use existing
            </button>
          </div>

          {mode === 'create' ? (
            <>
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Discount percentage (1–100)
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  placeholder="25"
                  className="w-full px-3 py-2 border rounded-md bg-background"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Duration (months, 1–12)
                </label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                />
              </div>
            </>
          ) : (
            <div>
              <label className="text-sm font-medium mb-1 block">
                Stripe coupon ID
              </label>
              <input
                type="text"
                value={couponId}
                onChange={(e) => setCouponId(e.target.value)}
                placeholder="e.g. Tr25hDiY"
                className="w-full px-3 py-2 border rounded-md bg-background font-mono"
              />
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={applyDiscount.isPending}
              className="flex-1"
            >
              {applyDiscount.isPending ? 'Applying...' : 'Apply discount'}
            </Button>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
